'use client';

import React, { useRef } from "react";
import Headline from "@/components/ui/Headline/Headline";
import { LanguageType, LanguageDirection } from "@/lib/types";
import Benefits from "@/components/ui/Benefits/Benefits";
import { Icon } from "@phosphor-icons/react";
import { gsap } from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import { useIsLg, useIsTb } from '@/contexts/BreakpointContext';

gsap.registerPlugin(useGSAP, ScrollTrigger);

export interface Benefit {
  heading: string;
  text: string;
  icon: Icon;
}

interface FoundingBenefitsSectionProps {
  language?: LanguageType;
  headlineText: string;
  subheadingText: string;
  paragraphText: string;
  benefits: Benefit[];
}

const LANGUAGE_TO_DIRECTION: Record<LanguageType, LanguageDirection> = {
  english: "ltr",
  arabic: "rtl"
};

const FoundingBenefitsSection: React.FC<FoundingBenefitsSectionProps> = ({
  language = "english",
  headlineText,
  subheadingText,
  paragraphText,
  benefits,
}) => {
  const isTb = useIsTb();
  const isLg = useIsLg();
  const benefitsGridRef = useRef<HTMLDivElement>(null);
  const sectionRef = useRef<HTMLElement>(null);
  const bgCircleRef = useRef<HTMLDivElement>(null);
  const bgLowerRef = useRef<HTMLDivElement>(null);
  useGSAP(() => {
    const benefitsGrid = benefitsGridRef.current;

    if (!benefitsGrid) return;

    const benefitItems = benefitsGrid.children;
    if (!benefitItems || benefitItems.length === 0) return;

    // Set initial states
    gsap.set(benefitItems, {
      autoAlpha: 0,
      filter: 'blur(30px)',
      force3D: true,
    });

    if (isLg) {
      // DESKTOP LOGIC - stagger all benefits together
      gsap.to(benefitItems, {
        autoAlpha: 1,
        filter: 'blur(0px)',
        duration: 1.0,
        stagger: 0.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: benefitsGrid,
          start: "top 70%",
          toggleActions: "play none none none"
        },
        force3D: true,
      });

    } else {
      // MOBILE LOGIC - separate trigger for each benefit
      Array.from(benefitItems).forEach((item) => {
        gsap.to(item, {
          autoAlpha: 1,
          filter: 'blur(0px)',
          duration: 0.9,
          ease: "power2.out",
          scrollTrigger: {
            trigger: item,
            start: "top 80%",
            toggleActions: "play none none none"
          },
          force3D: true,
        });
      });
    }

  }, { dependencies: [headlineText, benefits.length, isLg], revertOnUpdate: true });

  // Animate bottom border-radius of background elements on scroll
  useGSAP(() => {
    const targets = [bgCircleRef.current, bgLowerRef.current, sectionRef.current].filter(Boolean);
    if (targets.length === 0) return;

    const endRadius = isLg ? 56 : 24;

    gsap.fromTo(targets, {
      borderBottomLeftRadius: 0,
      borderBottomRightRadius: 0,
    }, {
      borderBottomLeftRadius: endRadius,
      borderBottomRightRadius: endRadius,
      ease: "none",
      scrollTrigger: {
        trigger: sectionRef.current,
        start: "bottom bottom",
        end: "bottom 75%",
        scrub: true,
      },
    });
  }, { dependencies: [isLg], revertOnUpdate: true });

  return (
    <section
      ref={sectionRef}
      style={{
        direction: LANGUAGE_TO_DIRECTION[language],
        paddingTop: isTb ? 'calc(13.5rem + 80px)' : 'calc(7rem + 80px)',
        marginTop: '-4.5rem'
      }}

      // lg:pt-[13.5rem]
        // bg-[#6d5941]
      className="
        w-full flex items-center justify-center
        padding-main-x-mobile
        padding-main-x-desktop
        pb-[3.5rem]
        lg:pb-[7rem]
        relative
        rounded-[24px] lg:rounded-[56px]
      "
    >
      {/* Background Images */}
      <div ref={bgCircleRef} className="bg-partial-circle-wrapper rounded-b-[24px] lg:rounded-b-[56px]">
        <div className="bg-partial-circle"></div>
      </div>

      <div ref={bgLowerRef} className="bg-lower-part rounded-b-[24px] lg:rounded-b-[56px]">


      </div>

      {/* <div
        // ref={shadowRef}
        className="absolute w-full aspect-3/4 tb:aspect-3/2 z-10 opacity-[8%] tb:opacity-10 pointer-events-none"
        style={{
          top: '0px',
          transform: '',
          backgroundImage: `url(./Website-Shadow-Overlay.avif)`,
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
          mixBlendMode: 'darken'
        }}
      /> */}
      <div className="w-full z-10 container-small flex items-center justify-center">
        <div className="w-full flex flex-col gap-[3.5rem] lg:gap-[4.5rem] items-center justify-center">

          {/* Headline and Benefits Section */}
          <div className="w-full flex flex-col gap-[2.5rem] items-center justify-center">
            <div className="flex items-center justify-center w-full">
              <Headline
                subheading={subheadingText}
                heading={headlineText}
                type="long"
                alignment="center"
                paragraph={paragraphText}
                language={language}
                textColor="text-text-reversed"
                reversedParagraphColor={true}
                shouldAnimate={true}
              />
            </div>

          </div>

          <div className="w-full flex items-center justify-center">
            <div
              ref={benefitsGridRef}
              className="
                w-full
                gap-y-[2rem] lg:gap-y-[2.5rem] gap-x-[2.5rem]
                grid
                [grid-template-columns:repeat(auto-fit,minmax(min(100%,23rem),1fr))]
              ">
              {benefits.map((benefit, index) => (
                <div key={index} className="flex items-center justify-center">
                  <Benefits
                    heading={benefit.heading}
                    text={benefit.text}
                    textColor="text-text-reversed"
                    icon={benefit.icon}
                  />
                </div>
              ))}
            </div>
          </div>

        </div>
      </div>
    </section>
  );
};

export default FoundingBenefitsSection;
